import customerService from '../../../services/customerService';
import authService from '../../../services/authService';

export const customerActions = {
  // Handle individual customer deletion
  handleDeleteCustomer: async (
    customer, 
    getFranchiseeId, 
    setCustomers, 
    setCustomerCount, 
    setPagination, 
    showNotification
  ) => {
    const franchiseeId = getFranchiseeId();
    
    if (!franchiseeId) {
      showNotification('No franchisee ID found', 'error');
      return;
    }

    if (!window.confirm(`Delete customer "${customer.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await customerService.deleteCustomer(franchiseeId, customer.id);
      
      // Remove customer from the list in real-time
      setCustomers(prevCustomers => prevCustomers.filter(c => c.id !== customer.id));
      
      // Update total count
      setCustomerCount(prevCount => prevCount - 1);
      setPagination(p => ({ ...p, total: p.total - 1 }));
      
      // Show success message
      showNotification(`Customer "${customer.name}" deleted successfully`, 'success');
      
    } catch (error) {
      console.error('Error deleting customer:', error);
      showNotification(`Failed to delete customer: ${error.message}`, 'error');
    }
  },

  // Handle bulk customer deletion (admin only)
  handleDeleteAllCustomers: async (
    userProfile,
    customerCount,
    customers,
    getFranchiseeId,
    setCustomers,
    setCustomerCount,
    setPagination,
    setCurrentPage,
    showNotification
  ) => {
    // Only allow if admin is impersonating (same logic as Return to Admin button)
    if (!authService.hasAdminReturn() || userProfile?.role === 'admin') {
      showNotification('Only admin impersonating franchisee can delete all customers', 'error');
      return;
    }
    
    const franchiseeId = getFranchiseeId();
    
    if (!franchiseeId) {
      showNotification('No franchisee context found', 'error');
      return;
    }

    const totalCustomers = customerCount || customers.length;
    const confirmed = window.confirm(
      `⚠️ DELETE ALL CUSTOMERS?\n\n` +
      `This will permanently delete ALL ${totalCustomers} customers for this franchisee from your local database.\n\n` +
      `This action:\n` +
      `• CANNOT be undone\n` +
      `• Will NOT affect QuickBooks\n` +
      `• Is for testing purposes only\n\n` +
      `Type "DELETE ALL" to confirm:`
    );

    if (!confirmed) {
      return;
    }

    const confirmText = prompt('Type "DELETE ALL" to confirm:');
    if (confirmText !== 'DELETE ALL') {
      showNotification('Deletion cancelled - confirmation text did not match', 'error');
      return;
    }

    try {
      const result = await customerService.deleteAllCustomers(franchiseeId);
      
      // Clear the customer list in real-time
      setCustomers([]);
      setCustomerCount(0);
      setPagination({ page: 1, limit: 50, total: 0, pages: 0 });
      setCurrentPage(1);
      
      // Show success message
      showNotification(`Successfully deleted ${result.deleted} customers`, 'success');
      
    } catch (error) {
      console.error('Error deleting all customers:', error);
      showNotification(`Failed to delete customers: ${error.message}`, 'error');
    }
  },

  // Handle real-time customer updates (Page 1 only)
  handleNewCustomers: (
    newCustomers, 
    progressData, 
    currentPage, 
    debouncedSearchTerm, 
    setCustomers, 
    setCustomerCount, 
    setPagination, 
    setHasNewRecords
  ) => {
    if (currentPage === 1 && !debouncedSearchTerm) {
      // Only add to Page 1 and when not searching
      console.log('📊 Adding new customers to list:', newCustomers);
      
      setCustomers(prev => {
        // Add new customers to the beginning (latest_qb_activity DESC order)
        const existingIds = new Set(prev.map(c => c.id));
        const uniqueNewCustomers = newCustomers.filter(c => !existingIds.has(c.id));
        
        if (uniqueNewCustomers.length > 0) {
          // Update total count
          setCustomerCount(count => count + uniqueNewCustomers.length);
          setPagination(p => ({ ...p, total: p.total + uniqueNewCustomers.length }));
          
          return [...uniqueNewCustomers, ...prev].slice(0, 50); // Keep only 50 items per page
        }
        return prev;
      });
    } else {
      // Show notification for other pages
      setHasNewRecords(true);
    }
  }
};